import { Spinner, TextInputProps } from "flowbite-react"
import moment from "moment"
import React, { useMemo, useState } from "react"
import DatePicker, { ReactDatePickerProps } from "react-datepicker"

import "react-datepicker/dist/react-datepicker.css"
import RightArrow from "@icons/RightArrow"
import { toLocalDate } from "@mono/essentials/utils/Common"

import styles from "./MonoInlineDatePicker.module.css"

interface MonoDatePickerProps extends Omit<ReactDatePickerProps, "selectsRange"> {
  inputFieldProps?: TextInputProps
  loading?: boolean
  showSpinner?: boolean
  containerClassName?: string
  isSelectedDate?: Date | null
  headerContent?: React.ReactNode
}

const MonoInlineDatePicker = ({
  inputFieldProps,
  onChange,
  popperClassName,
  loading = false,
  value,
  showSpinner = false,
  containerClassName,
  isSelectedDate,
  onMonthChange,
  headerContent,
  ...rest
}: MonoDatePickerProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    isSelectedDate ? moment(isSelectedDate, "DD-MM-YY").toDate() : toLocalDate(moment())
  )
  const onValueChange = (
    date: Date | null,
    event: React.SyntheticEvent<any, Event> | undefined
  ) => {
    setSelectedDate(date)
    onChange?.(date, event)
  }

  const handleMonthChange = (date) => {
    onMonthChange?.(date)
    setSelectedDate(date)
  }

  return (
    <div className={`w-full ${containerClassName ?? ""} ${loading ? styles.loading : ""}`}>
      <link
        rel={"stylesheet"}
        href={
          "https://cdnjs.cloudflare.com/ajax/libs/react-datepicker/4.10.0/react-datepicker.min.css"
        }
      />
      <DatePicker
        {...rest}
        selected={selectedDate}
        onChange={onValueChange}
        onMonthChange={handleMonthChange}
        calendarClassName={`${styles.datePicker} ${popperClassName ?? ""}`}
        renderDayContents={(day: number, date?: Date) => (
          <span data-testid={`calendar-day-${day}`}>{day}</span>
        )}
        renderCustomHeader={({ decreaseMonth, increaseMonth, monthDate }) => {
          const year = monthDate?.getFullYear()
          const month = monthDate?.getMonth()
          const date = moment().set({
            year,
            month,
            date: 2,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          })

          return (
            <>
              {loading && showSpinner ? (
                <Spinner
                  size={"md"}
                  light
                  className={"absolute top-[160%] left-[46%] z-1 text-primary-400 fill-white"}
                />
              ) : null}
              {headerContent}
              <div className={"flex flex-row items-start justify-between gap-1 p-0.5"}>
                <RightArrow
                  onClick={decreaseMonth}
                  width={20}
                  height={20}
                  className={"rotate-180 transform cursor-pointer text-neutral-800"}
                />
                <p className={"text15Medium text-neutral-800"}>{date.format("MMMM YYYY")}</p>
                <RightArrow
                  onClick={increaseMonth}
                  width={20}
                  height={20}
                  className={"cursor-pointer text-neutral-800"}
                />
              </div>
            </>
          )
        }}
        peekNextMonth={false}
        showPreviousMonths={false}
      />
    </div>
  )
}

export default MonoInlineDatePicker
